// src/web/lib/query.ts
import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes - good for most data
			gcTime: 1000 * 60 * 30, // 30 minutes cache retention
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors (client errors)
				if (error instanceof Error) {
					// Check for HTTP status codes in error message (e.g., "400", "401", "403", "404")
					const statusMatch = error.message.match(/\b(4\d{2})\b/)
					if (statusMatch) {
						return false
					}
					// Also check for common client error keywords
					const clientErrorKeywords = ['unauthorized', 'forbidden', 'not found', 'bad request']
					if (
						clientErrorKeywords.some((keyword) => error.message.toLowerCase().includes(keyword))
					) {
						return false
					}
				}
				// Retry up to 2 times for other errors
				return failureCount < 2
			},
			refetchOnWindowFocus: false,
			refetchOnReconnect: true,
		},
		mutations: {
			retry: 0,
			onError: (error) => {
				console.error('Mutation error:', error)
			},
		},
	},
})

// Query keys factory for consistent key management
export const queryKeys = {
	// Auth
	auth: {
		me: ['auth', 'me'] as const,
	},

	// Workspaces
	workspaces: {
		all: ['workspaces'] as const,
		details: (id: string) => ['workspaces', 'details', id] as const,
		settings: (id: string) => ['workspaces', 'settings', id] as const,
		members: ['workspaces', 'members'] as const,
	},

	// Assets
	assets: {
		all: ['assets'] as const,
		// Granular query keys for better cache invalidation
		list: (filters: {
			query?: string
			folder?: string
			fieldFilters?: string
			lifecycleState?: string
			fileType?: string
			sortBy?: string
			sortOrder?: 'asc' | 'desc'
		}) => ['assets', 'list', filters] as const,
		// Separate infinite query with same filters
		infinite: (filters: {
			query?: string
			folder?: string
			fieldFilters?: string
			lifecycleState?: string
			fileType?: string
			sortBy?: string
			sortOrder?: 'asc' | 'desc'
		}) => ['assets', 'infinite', filters] as const,
		detail: (id: string) => ['assets', 'detail', id] as const,
		history: (id: string) => ['assets', 'history', id] as const,
		storageStats: ['assets', 'stats'] as const,
	},

	// Fields
	fields: {
		all: ['fields'] as const,
		list: () => ['fields', 'list'] as const,
		detail: (id: string) => ['fields', 'detail', id] as const,
		options: (id: string, parentId?: string) => ['fields', 'options', id, parentId] as const,
		folderPreviews: (id: string, parentId?: string) =>
			['fields', 'folderPreviews', id, parentId] as const,
	},

	// Collections
	collections: {
		all: ['collections'] as const,
		list: () => ['collections', 'list'] as const,
		detail: (id: string) => ['collections', 'detail', id] as const,
		forAsset: (assetId: string) => ['collections', 'forAsset', assetId] as const,
	},

	// Shares
	shares: {
		all: ['shares'] as const,
		forFile: (fileId: string) => ['shares', fileId] as const,
	},

	// Folder shares
	folderShares: {
		all: ['folderShares'] as const,
		forFolder: (folderId: string) => ['folderShares', folderId] as const,
		publicInfo: (token: string) => ['folderShares', 'public', 'info', token] as const,
		publicFolders: (token: string, parentId?: string) =>
			['folderShares', 'public', 'folders', token, parentId] as const,
		publicAssets: (token: string, folderId?: string, page?: number) =>
			['folderShares', 'public', 'assets', token, folderId, page] as const,
		publicBreadcrumbs: (token: string, folderId?: string) =>
			['folderShares', 'public', 'breadcrumbs', token, folderId] as const,
	},

	// Browse (folder navigation)
	browse: {
		path: (fieldId: string, folderId: string) => ['browse', 'path', fieldId, folderId] as const,
		count: (fieldId: string, folderId: string) => ['browse', 'count', fieldId, folderId] as const,
	},

	// Uploads
	uploads: {
		job: (id: string) => ['uploads', 'job', id] as const,
		batch: (ids: string[]) => ['uploads', 'batch', ids] as const,
	},

	// Conversion (video/image processing)
	conversion: {
		status: (fileId: string, quality: string) => ['conversion', fileId, quality] as const,
		list: (fileId: string) => ['conversion', 'list', fileId] as const,
	},

	// Activity feed (global audit log)
	activity: {
		all: ['activity'] as const,
		list: (limit?: number) => ['activity', 'list', limit] as const,
	},

	// Asset comments
	comments: {
		all: ['comments'] as const,
		forAsset: (assetId: string) => ['comments', 'asset', assetId] as const,
	},

	// In-app notifications (header bell)
	notifications: {
		all: ['notifications'] as const,
		list: ['notifications', 'list'] as const,
	},
}

// Helper functions for creating optimized query keys
export const createAssetQueryKey = (
	query?: string,
	folder?: string,
	fieldFilters?: string,
	lifecycleState?: string,
	fileType?: string,
	sortBy?: string,
	sortOrder?: 'asc' | 'desc',
	page?: number,
	pageSize?: number
) => {
	// Only include non-undefined values to prevent unnecessary cache misses
	const filters: Record<string, unknown> = {}
	if (query !== undefined) filters.query = query
	if (folder !== undefined) filters.folder = folder
	if (fieldFilters !== undefined) filters.fieldFilters = fieldFilters
	if (lifecycleState !== undefined) filters.lifecycleState = lifecycleState
	if (fileType !== undefined) filters.fileType = fileType
	if (sortBy !== undefined) filters.sortBy = sortBy
	if (sortOrder !== undefined) filters.sortOrder = sortOrder
	if (page !== undefined) filters.page = page
	if (pageSize !== undefined) filters.pageSize = pageSize

	return queryKeys.assets.list(filters)
}

export const createAssetInfiniteQueryKey = (
	query?: string,
	folder?: string,
	fieldFilters?: string,
	lifecycleState?: string,
	fileType?: string,
	sortBy?: string,
	sortOrder?: 'asc' | 'desc'
) => {
	// Same logic as createAssetQueryKey but for infinite queries
	const filters: Record<string, unknown> = {}
	if (query !== undefined) filters.query = query
	if (folder !== undefined) filters.folder = folder
	if (fieldFilters !== undefined) filters.fieldFilters = fieldFilters
	if (lifecycleState !== undefined) filters.lifecycleState = lifecycleState
	if (fileType !== undefined) filters.fileType = fileType
	if (sortBy !== undefined) filters.sortBy = sortBy
	if (sortOrder !== undefined) filters.sortOrder = sortOrder

	return queryKeys.assets.infinite(filters)
}

// Query configuration presets for different data types
export const queryConfig = {
	// Static data that rarely changes
	static: {
		staleTime: 1000 * 60 * 60, // 1 hour
		gcTime: 1000 * 60 * 60 * 24, // 24 hours
	},

	// User-specific data
	user: {
		staleTime: 1000 * 60 * 15, // 15 minutes
		gcTime: 1000 * 60 * 60, // 1 hour
	},

	// Dynamic lists that change frequently
	dynamic: {
		staleTime: 1000 * 60 * 2, // 2 minutes
		gcTime: 1000 * 60 * 15, // 15 minutes
	},

	// Real-time data
	realtime: {
		staleTime: 1000 * 30, // 30 seconds
		gcTime: 1000 * 60 * 5, // 5 minutes
		refetchOnWindowFocus: true,
	},
}
